import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import PropTypes from "prop-types";
import withStyles from "@material-ui/core/styles/withStyles";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogFooter from "components/Dialog/DialogFooter.jsx";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";
import Chip from "@material-ui/core/Chip";

import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import Card from "components/Card/Card.jsx";
import CardHeader from "components/Card/CardHeader.jsx";
import CardBody from "components/Card/CardBody.jsx";
import Button from "components/CustomButtons/Button.jsx";
import CustomInput from "components/CustomInput/CustomInput.jsx";
import DialogHeader from "components/Dialog/DialogHeader.jsx";

import api from "api/index";
import ViewToolbar from "components/Toolbar/ViewToolbar.jsx";

const styles = {
  header: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  formControl: { width: "100%", marginTop: "16px" },
  errorMsg: { color: "#f44336", fontSize: "13px" },
  noticeCard: { border: "1px solid #e0e0e0", borderRadius: "8px", padding: "16px", marginBottom: "12px" },
  noticeTitle: { fontWeight: "bold", color: "#1a237e", fontSize: "15px", margin: "0 0 6px" },
  noticeBody: { color: "#444", fontSize: "13px", lineHeight: "1.6", margin: "0 0 8px" },
  noticeMeta: { fontSize: "11px", color: "#999" }
};

const CATEGORIES = ["general", "academic", "fees", "exams", "hr", "events"];
const AUDIENCE = ["all", "students", "staff", "lecturers"];
const CATEGORY_COLORS = {
  general:  { bg: "#e3f2fd", text: "#0d47a1" },
  academic: { bg: "#e8f5e9", text: "#2e7d32" },
  fees:     { bg: "#fff8e1", text: "#e65100" },
  exams:    { bg: "#fce4ec", text: "#880e4f" },
  hr:       { bg: "#fafafa", text: "#333" },
  events:   { bg: "#f3e5f5", text: "#6a1b9a" },
};

const BLANK = { title: "", message: "", category: "general", audience: "all", pinned: false };

class NoticeBoard extends Component {
  state = {
    notices: [],
    loading: true,
    open: false,
    editing: null,
    form: { ...BLANK },
    saving: false,
    error: null,
    categoryFilter: ""
  };

  componentDidMount() { this.fetchNotices(); }

  fetchNotices = async () => {
    this.setState({ loading: true });
    try {
      const { categoryFilter } = this.state;
      const params = { limit: 50, sort: "pinned,createdAt", order: "desc" };
      if (categoryFilter) params.category = categoryFilter;
      const res = await api.get("/notices", { params });
      const d = res.data.data || res.data;
      this.setState({ notices: Array.isArray(d) ? d : d.data || [], loading: false });
    } catch (e) { this.setState({ loading: false }); }
  };

  handleChange = field => e => { const v = e.target.value; this.setState(prev => ({ form: { ...prev.form, [field]: v } })); };

  handleSave = async () => {
    const { form, editing } = this.state;
    if (!form.title || !form.message) { this.setState({ error: "Title and message are required." }); return; }
    this.setState({ saving: true, error: null });
    try {
      if (editing) await api.put(`/notices/${editing}`, form);
      else await api.post("/notices", form);
      this.setState({ open: false, saving: false });
      this.fetchNotices();
    } catch (e) {
      this.setState({ error: e.response?.data?.message || "Failed to save.", saving: false });
    }
  };

  handleDelete = async (id) => {
    if (!window.confirm("Delete this notice?")) return;
    try {
      await api.delete(`/notices/${id}`);
      this.fetchNotices();
    } catch (e) { /* silent */ }
  };

  handlePin = async (id, pinned) => {
    try {
      await api.patch(`/notices/${id}`, { pinned: !pinned });
      this.fetchNotices();
    } catch (e) { /* silent */ }
  };

  openEdit = (n) => {
    this.setState({
      open: true, editing: n.id, error: null,
      form: { title: n.title, message: n.message, category: n.category || "general", audience: n.audience || "all", pinned: n.pinned || false }
    });
  };

  render() {
    const { classes } = this.props;
    const { notices, loading, open, editing, form, saving, error, categoryFilter } = this.state;

    return (
      <GridContainer>
        <GridItem xs={12}>
          <Card>
            <CardHeader color="primary" style={{ margin: -8, padding: "24px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <h4 style={{ margin: 0, color: "#fff", fontWeight: "bold" }}>Notice Board</h4>
                  <p style={{ margin: "2px 0 0", color: "rgba(255,255,255,0.8)", fontSize: "13px" }}>
                    Announcements and communications
                  </p>
                </div>
                <div style={{ display: "flex", gap: "8px" }}>
                  <Button size="sm" color="white" onClick={() => this.setState({ open: true, editing: null, form: { ...BLANK }, error: null })}>
                    + Post Notice
                  </Button>
                </div>
            </CardHeader>
            <CardBody>
              <ViewToolbar tabs={[]} actions={[]} />
              {/* Filter */}
              <div style={{ display: "flex", gap: "12px", marginBottom: "16px", flexWrap: "wrap" }}>
                {["", ...CATEGORIES].map(cat => (
                  <Button
                    key={cat || "all"}
                    size="sm"
                    onClick={() => this.setState({ categoryFilter: cat }, this.fetchNotices)}
                    style={{
                      background: categoryFilter === cat ? "#1a237e" : "#f5f5f5",
                      color: categoryFilter === cat ? "#fff" : "#333",
                      boxShadow: "none"
                    }}
                  >
                    {cat || "All"}
                  </Button>
                ))}
              </div>

              {loading ? (
                <div style={{ textAlign: "center", padding: "30px" }}><CircularProgress /></div>
              ) : notices.length > 0 ? (
                notices.map(n => {
                  const cc = CATEGORY_COLORS[n.category] || { bg: "#f5f5f5", text: "#333" };
                  return (
                    <div key={n.id} className={classes.noticeCard} style={{ borderLeft: `4px solid ${cc.text}` }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap", marginBottom: "6px" }}>
                            {n.pinned && (
                              <span style={{ fontSize: "11px", fontWeight: "bold", color: "#e65100" }}>&#128204; PINNED</span>
                            )}
                            <Chip label={n.category || "General"} style={{ background: cc.bg, color: cc.text, fontSize: "11px", height: "20px" }} />
                            <Chip label={n.audience || "All"} style={{ background: "#f5f5f5", color: "#555", fontSize: "11px", height: "20px" }} />
                          </div>
                          <p className={classes.noticeTitle}>{n.title}</p>
                          <p className={classes.noticeBody}>{n.message}</p>
                          <p className={classes.noticeMeta}>
                            Posted by {n.author?.name || n.createdBy || "Admin"} &middot; {n.createdAt ? new Date(n.createdAt).toLocaleDateString("en-UG") : ""}
                          </p>
                        </div>
                        <div style={{ display: "flex", gap: "4px", flexShrink: 0, marginLeft: "12px" }}>
                          <Button size="sm" simple onClick={() => this.handlePin(n.id, n.pinned)} title={n.pinned ? "Unpin" : "Pin"}>
                            &#128204;
                          </Button>
                          <Button size="sm" color="info" simple onClick={() => this.openEdit(n)}>Edit</Button>
                          <Button size="sm" color="danger" simple onClick={() => this.handleDelete(n.id)}>Del</Button>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <p style={{ textAlign: "center", color: "#999", padding: "30px" }}>No notices posted yet.</p>
              )}
            </CardBody>
          </Card>
        </GridItem>

        <Dialog open={open} onClose={() => this.setState({ open: false })} maxWidth="sm" fullWidth>
          <DialogHeader title={editing ? "Edit Notice" : "Post New Notice"} />
          <DialogContent>
            {error && <p className={classes.errorMsg}>{error}</p>}
            <CustomInput labelText="Title *" id="noticeTitle" formControlProps={{ fullWidth: true }}
              inputProps={{ value: form.title, onChange: this.handleChange("title") }} />
            <div style={{ marginTop: "16px" }}>
              <label style={{ fontSize: "12px", color: "#aaa", textTransform: "uppercase" }}>Body *</label>
              <textarea
                rows={5}
                value={form.message}
                onChange={this.handleChange("message")}
                style={{ width: "100%", marginTop: "4px", padding: "8px 12px", border: "1px solid #ddd", borderRadius: "4px", fontSize: "14px", resize: "vertical", boxSizing: "border-box" }}
              />
            </div>
            <GridContainer>
              <GridItem xs={6}>
                <FormControl className={classes.formControl}>
                  <InputLabel>Category</InputLabel>
                  <Select value={form.category} onChange={this.handleChange("category")}>
                    {CATEGORIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                  </Select>
                </FormControl>
              </GridItem>
              <GridItem xs={6}>
                <FormControl className={classes.formControl}>
                  <InputLabel>Audience</InputLabel>
                  <Select value={form.audience} onChange={this.handleChange("audience")}>
                    {AUDIENCE.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
                  </Select>
                </FormControl>
              </GridItem>
            </GridContainer>
          </DialogContent>
          <DialogFooter>
            <Button outlined onClick={() => this.setState({ open: false })} disabled={saving}>Cancel</Button>
            <Button color="primary" onClick={this.handleSave} disabled={saving}>
              {saving ? <CircularProgress size={18} style={{ color: "#fff" }} /> : "Post"}
            </Button>
          </DialogFooter>
        </Dialog>
      </GridContainer>
    );
  }
}

NoticeBoard.propTypes = { classes: PropTypes.object.isRequired };
export default withRouter(withStyles(styles)(NoticeBoard));
